import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {useMotionSafeTransitionStyles} from './floating/useMotionSafeTransitionStyles';
import type {StatusMessageProps} from './StatusMessage';
import StatusMessage from './StatusMessage';

const TRANSITION_MS = 200;

// How long the toast for a success stays up. An error has no time limit
// (WCAG 2.2.1): it says what to do next, and somebody who reads slowly or
// through a magnifier would lose it halfway through.
export const TOAST_VISIBLE_MS = 2000;

// Matches the z-index `Layer` gives its portal, as `ContextMenu` and
// `TooltipContainer` do.
const LAYER_Z_INDEX = 100;

// The tooltip's colors: white is 12.6:1 on this fill over a white page, and
// higher over anything darker.
const Bubble = styled('div', ({$below}: {$below: boolean}) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  borderRadius: 3,
  color: '#fff',
  fontFamily: DEFAULT_FONT,
  fontSize: 14,
  lineHeight: '20px',
  padding: '4px 8px',
  transformOrigin: $below ? 'top center' : 'bottom center',
}));

type Props = Omit<StatusMessageProps, 'pendingMessage' | 'visuallyHidden'> & {
  /** What the toast appears beside: the control that acted, usually. */
  anchor: Element | null;
  /**
   * Changes with each new message, so that the toast appears again for a
   * message with the same words.
   */
  messageKey?: React.Key;
};

/**
 * A status message that appears beside `anchor`, for a control with no room
 * around it for a message, such as an icon in a field's end cap. A success
 * goes after a moment. An error stays until the user presses Escape or moves
 * on: presses anywhere, or moves focus away from `anchor`.
 *
 * The toast floats in a portal, so it moves nothing on the page. It is only the
 * visible copy, and it is hidden from assistive technology: a region added
 * together with its text is not reliably announced. The `StatusMessage`
 * rendered in place, off screen, is what a screen reader announces, and it
 * keeps the message after the toast goes.
 */
const StatusToast = ({anchor, message, messageKey, type}: Props) => {
  const hasMessage = message != null && message !== false && message !== '';
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!hasMessage) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (type === 'negative') {
      return;
    }
    const timer = window.setTimeout(() => setOpen(false), TOAST_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [hasMessage, messageKey, type]);

  const isOpenError = open && type === 'negative';
  React.useEffect(() => {
    if (!isOpenError) {
      return;
    }
    const close = () => setOpen(false);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    // Focus that stays on the control, or comes back to it, is still reading.
    const handleFocusIn = (e: FocusEvent) => {
      if (!anchor?.contains(e.target as Node | null)) {
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', close);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [anchor, isOpenError]);

  const {context, floatingStyles, placement, refs} = useFloating({
    open,
    elements: {reference: anchor},
    // Above the control, so that the toast never covers it while it has focus.
    placement: 'top',
    middleware: [offset(6), flip({padding: 8}), shift({padding: 8})],
    whileElementsMounted: autoUpdate,
  });
  const {isMounted, styles: transitionStyles} = useMotionSafeTransitionStyles(
    context,
    {
      duration: TRANSITION_MS,
      initial: {opacity: 0, transform: 'scale(0.9)'},
      open: {opacity: 1, transform: 'scale(1)'},
    },
  );

  return (
    <>
      <StatusMessage message={message} type={type} visuallyHidden />
      {isMounted && (
        <FloatingPortal>
          <div
            aria-hidden="true"
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              // Measured at its own width, not the room left at its position,
              // so it does not wrap differently each time it moves.
              maxWidth: 300,
              pointerEvents: 'none',
              width: 'max-content',
              zIndex: LAYER_Z_INDEX,
            }}
          >
            <Bubble
              $below={placement.startsWith('bottom')}
              __forceStyle={transitionStyles}
            >
              {message}
            </Bubble>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default StatusToast;
